import React, { useEffect, useState } from "react";
import { Table } from "react-super-responsive-table";
import { Card, Col, Row, Button, UncontrolledTooltip } from "reactstrap";
import "../../assets/scss/style.css";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { Modal, Form } from "react-bootstrap";
import {
  getAllSubscriptionPackages,
  createSubscriptionPackage,
  updateSubscriptionPackage,
  deleteSubscriptionPackage,
  createStripeProduct,
  toggleSubscriptionRequired,
  getSubscriptionRequired,
} from "services/services";
import { successMsgHandler } from "common/successMsgHandler";
import { errorMsgHandler } from "common/errorMsgHandler";
import Swal from "sweetalert2";

const SubscriptionPackages = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [show, setShow] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState("");
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  const handleClose = () => {
    setShow(false);
    setIsEdit(false);
    setEditId("");
    formik.resetForm();
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    description: Yup.string().required("Description is required"),
    price: Yup.number()
      .required("Price is required")
      .min(0, "Price must be greater than or equal to 0"),
    yearlyPrice: Yup.number()
      .required("Yearly price is required")
      .min(0, "Yearly price must be greater than or equal to 0"),
    perAgentPrice: Yup.number()
      .required("Per agent price is required")
      .min(0, "Per agent price must be greater than or equal to 0"),
    maxAgents: Yup.string()
      .required("Max agents is required"),
    trialDays: Yup.number()
      .required("Trial period days is required")
      .min(0, "Trial period days must be greater than or equal to 0")
      .integer("Trial period days must be a whole number"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      price: "",
      yearlyPrice: "",
      perAgentPrice: "",
      maxAgents: "",
      trialDays: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = {
          name: values.name,
          description: values.description,
          price: parseFloat(values.price),
          yearlyPrice: parseFloat(values.yearlyPrice),
          perAgentPrice: parseFloat(values.perAgentPrice),
          maxAgents: values.maxAgents, // Store as string to preserve ranges like "0-50"
          trialDays: parseInt(values.trialDays, 10),
        };

        let res;
        if (isEdit && editId) {
          res = await updateSubscriptionPackage(editId, payload);
        } else {
          res = await createSubscriptionPackage(payload);
        }

        // Handle nested response structure similar to GET
        const status = res?.code || res?.status || res?.data?.code || res?.data?.status;
        const message = res?.message || res?.data?.message || 
                       (isEdit
                         ? "Subscription package updated successfully"
                         : "Subscription package created successfully");

        if (status === 200) {
          // Extract packageId from response for new packages
          if (!isEdit) {
            const packageId = 
              res?.data?.data?._id || 
              res?.data?.data?.id || 
              res?.data?._id || 
              res?.data?.id || 
              res?._id || 
              res?.id;

            if (packageId) {
              try {
                // Create Stripe product for the new package
                await createStripeProduct(packageId);
                console.log("Stripe product created successfully for package:", packageId);
              } catch (stripeError) {
                console.error("Error creating Stripe product:", stripeError);
                // Show warning but don't fail the entire operation
                const stripeErrorMessage = 
                  stripeError?.response?.data?.message || 
                  stripeError?.response?.data?.error || 
                  stripeError?.message || 
                  "Package created but failed to create Stripe product";
                toast.error(`Package created but Stripe product creation failed: ${stripeErrorMessage}`);
              }
            } else {
              console.warn("Package created but packageId not found in response. Cannot create Stripe product.");
            }
          }

          toast.success(successMsgHandler(message));
          handleClose();
          fetchPackages();
        } else {
          const errorMsg = res?.data?.message || res?.message || "Something went wrong. Please try again.";
          toast.error(errorMsg);
        }
      } catch (error) {
        console.error("Error saving subscription package:", error);
        // Safely handle error - check if error has response property
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.error || 
                            error?.message || 
                            "Something went wrong. Please try again.";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const res = await getAllSubscriptionPackages();
      
      // Handle the nested response structure: res.data.data.packages
      let packagesList = [];
      
      // Check for the nested structure: data.data.packages
      if (res?.data?.data?.packages && Array.isArray(res.data.data.packages)) {
        packagesList = res.data.data.packages;
      }
      // Fallback to other possible structures
      else if (Array.isArray(res)) {
        packagesList = res;
      }
      else if (Array.isArray(res?.data)) {
        packagesList = res.data;
      }
      else if (Array.isArray(res?.packages)) {
        packagesList = res.packages;
      }
      else if (Array.isArray(res?.items)) {
        packagesList = res.items;
      }
      else if (res?.data?.data?.data && Array.isArray(res.data.data.data)) {
        packagesList = res.data.data.data;
      }
      
      // Filter to show only active packages (isActive === true)
      const activePackages = Array.isArray(packagesList) 
        ? packagesList.filter(pkg => pkg.isActive === true)
        : [];
      
      setPackages(activePackages);
    } catch (error) {
      console.error("Error fetching subscription packages:", error);
      toast.error(errorMsgHandler(error));
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionRequired = async () => {
    try {
      const res = await getSubscriptionRequired();
      console.log("📥 Subscription required status response:", res);
      
      // Handle nested response structure
      const subscriptionRequiredValue = 
        res?.data?.subscriptionRequired ?? 
        res?.subscriptionRequired ?? 
        res?.data?.data?.subscriptionRequired ?? 
        false;
      
      console.log("📊 Setting subscription required to:", subscriptionRequiredValue);
      setSubscriptionRequired(subscriptionRequiredValue);
    } catch (error) {
      console.error("Error fetching subscription required status:", error);
      // Default to false on error
      setSubscriptionRequired(false);
    }
  };

  useEffect(() => {
    fetchPackages();
    fetchSubscriptionRequired();
  }, []);

  const handleEdit = (pkg) => {
    setIsEdit(true);
    setEditId(pkg._id || pkg.id);
    formik.setValues({
      name: pkg.name || "",
      description: pkg.description || "",
      price: pkg.price || "",
      yearlyPrice: pkg.yearlyPrice || "",
      perAgentPrice: pkg.perAgentPrice || "",
      maxAgents: pkg.maxAgents || "",
      trialDays: pkg.trialDays || "",
    });
    setShow(true);
  };

  const handleDelete = async (pkgId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const res = await deleteSubscriptionPackage(pkgId);
        
        // Handle nested response structure similar to GET
        const status = res?.code || res?.status || res?.data?.code || res?.data?.status;
        const message = res?.message || res?.data?.message || "Subscription package deleted successfully";
        
        if (status === 200) {
          toast.success(successMsgHandler(message));
          fetchPackages();
        } else {
          toast.error(message || "Failed to delete subscription package");
        }
      } catch (error) {
        console.error("Error deleting subscription package:", error);
        // Safely handle error - check if error has response property
        const errorMessage = error?.response?.data?.message || 
                            error?.response?.data?.error || 
                            error?.message || 
                            "Failed to delete subscription package";
        toast.error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleSubscriptionRequired = async () => {
    if (toggleLoading) {
      console.log("⏸️ Toggle is loading, preventing action");
      return; // Prevent multiple clicks
    }
    
    const newValue = !subscriptionRequired;
    const previousValue = subscriptionRequired;
    
    console.log("🔄 Toggling from", previousValue, "to", newValue);
    
    // Optimistically update the UI
    setSubscriptionRequired(newValue);
    setToggleLoading(true);
    
    try {
      console.log("📡 Calling API with subscriptionRequired:", newValue);
      
      const res = await toggleSubscriptionRequired(newValue);
      console.log("✅ Toggle API response:", res);
      
      // Handle nested response structure
      const status = res?.code || res?.status || res?.data?.code || res?.data?.status;
      const message = res?.message || res?.data?.message || 
                     (newValue 
                       ? "Subscription requirement enabled successfully" 
                       : "Subscription requirement disabled successfully");
      
      console.log("📊 Response status:", status, "Message:", message);
      
      if (status === 200 || status === 201) {
        toast.success(successMsgHandler(message));
        console.log("✅ Toggle successful");
      } else {
        // Revert on error
        console.log("❌ Toggle failed, reverting to:", previousValue);
        setSubscriptionRequired(previousValue);
        const errorMsg = res?.data?.message || res?.message || "Failed to toggle subscription requirement";
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error("❌ Error toggling subscription required:", error);
      console.error("Error details:", {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      });
      // Revert on error
      setSubscriptionRequired(previousValue);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          "Failed to toggle subscription requirement";
      toast.error(errorMessage);
    } finally {
      setToggleLoading(false);
      console.log("🏁 Toggle operation completed");
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col lg="12">
            <Card className="card-transparent">
              <Row className="d-flex align-items-center mb-3">
                <Col lg="6">
                  <h5 className="text-color-gredient">Subscription Packages</h5>
                </Col>
                <Col lg="6" className="d-flex justify-content-end align-items-center gap-2">
                  <div className="d-flex align-items-center me-3">
                    <span className="me-2" style={{ fontSize: "14px", fontWeight: "500" }}>
                      Subscription Required:
                    </span>
                    <div
                      id="subscriptionRequiredToggleTooltip"
                      className="p-1 btn"
                      onClick={handleToggleSubscriptionRequired}
                      style={{
                        cursor: toggleLoading ? "not-allowed" : "pointer",
                        border: "none",
                        background: "transparent",
                        padding: "4px",
                      }}
                    >
                      <i
                        className={`mdi ${subscriptionRequired ? "mdi-toggle-switch" : "mdi-toggle-switch-off"} common-icons`}
                        style={{ 
                          fontSize: "30px",
                          color: subscriptionRequired ? "#01A54F" : "#6c757d"
                        }}
                      />
                      <UncontrolledTooltip placement="top" target="subscriptionRequiredToggleTooltip">
                        {subscriptionRequired ? "Disable Subscription Requirement" : "Enable Subscription Requirement"}
                      </UncontrolledTooltip>
                    </div>
                  </div>
                  <Button
                    className="btn"
                    onClick={() => setShow(true)}
                    style={{
                      backgroundColor: "#01A54F",
                      color: "#fff",
                      border: "none",
                      borderRadius: "6px",
                      padding: "8px 20px",
                      fontWeight: "500",
                    }}
                  >
                    <i
                      className="bx bx-plus"
                      style={{ marginRight: "6px" }}
                    ></i>
                    Add Package
                  </Button>
                </Col>
              </Row>

              <div className="table-responsive">
                <Table className="table common-table table-nowrap table-bordered text-center align-middle">
                  <thead>
                    <tr>
                      <th scope="col">Sr No</th>
                      <th scope="col">Name</th>
                      <th scope="col">Description</th>
                      <th scope="col">Price</th>
                      <th scope="col">Yearly Price</th>
                      <th scope="col">Per Agent Price</th>
                      <th scope="col">Max Agents</th>
                      <th scope="col">Trial Days</th>
                      <th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="9" className="text-center">
                          Loading...
                        </td>
                      </tr>
                    ) : packages.length > 0 ? (
                      packages.map((pkg, index) => (
                        <tr key={pkg._id || pkg.id || index}>
                          <td>{index + 1}</td>
                          <td>{pkg.name || "-"}</td>
                          <td
                            style={{
                              maxWidth: "200px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={pkg.description || "-"}
                          >
                            {pkg.description || "-"}
                          </td>
                          <td>${pkg.price || "0.00"}</td>
                          <td>${pkg.yearlyPrice || "0.00"}</td>
                          <td>${pkg.perAgentPrice || "0.00"}</td>
                          <td>{pkg.maxAgents || "-"}</td>
                          <td>{pkg.trialDays !== undefined && pkg.trialDays !== null ? `${pkg.trialDays} days` : "-"}</td>
                          <td>
                            <div className="d-flex align-items-center justify-content-center">
                              <div
                                id={`editTooltip-${pkg._id || pkg.id}`}
                                className="me-2 p-1 btn"
                                onClick={() => handleEdit(pkg)}
                              >
                                <i className="bx bx-edit-alt common-icons" />
                                <UncontrolledTooltip placement="top" target={`editTooltip-${pkg._id || pkg.id}`}>
                                  {"Edit"}
                                </UncontrolledTooltip>
                              </div>
                              <div
                                id={`delTooltip-${pkg._id || pkg.id}`}
                                className="p-1 btn"
                                onClick={() => handleDelete(pkg._id || pkg.id)}
                              >
                                <i className="mdi mdi-delete-outline common-icons" />
                                <UncontrolledTooltip placement="top" target={`delTooltip-${pkg._id || pkg.id}`}>
                                  {"Delete"}
                                </UncontrolledTooltip>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className="text-center">
                          No Records Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        size="lg"
        show={show}
        onHide={handleClose}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEdit ? "Edit Subscription Package" : "Add Subscription Package"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={formik.handleSubmit}>
            <div className="row">
              <div className="col-12 mb-3">
                <Form.Group controlId="name">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                    placeholder="Enter package name"
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formik.errors.name}
                    </div>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col-12 mb-3">
                <Form.Group controlId="description">
                  <Form.Label>Description *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.description}
                    placeholder="Enter package description"
                  />
                  {formik.touched.description && formik.errors.description ? (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formik.errors.description}
                    </div>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Group controlId="price">
                  <Form.Label>Price (Monthly) *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="price"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.price}
                    placeholder="0.00"
                  />
                  {formik.touched.price && formik.errors.price ? (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formik.errors.price}
                    </div>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Group controlId="yearlyPrice">
                  <Form.Label>Yearly Price *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="yearlyPrice"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.yearlyPrice}
                    placeholder="0.00"
                  />
                  {formik.touched.yearlyPrice && formik.errors.yearlyPrice ? (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formik.errors.yearlyPrice}
                    </div>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Group controlId="perAgentPrice">
                  <Form.Label>Per Agent Price *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="perAgentPrice"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.perAgentPrice}
                    placeholder="0.00"
                  />
                  {formik.touched.perAgentPrice && formik.errors.perAgentPrice ? (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formik.errors.perAgentPrice}
                    </div>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Group controlId="maxAgents">
                  <Form.Label>Max Agents *</Form.Label>
                  <Form.Control
                    type="text"
                    name="maxAgents"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.maxAgents}
                    placeholder="0 to 50"
                  />
                  {formik.touched.maxAgents && formik.errors.maxAgents ? (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formik.errors.maxAgents}
                    </div>
                  ) : null}
                </Form.Group>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Group controlId="trialDays">
                  <Form.Label>Trial Period Days *</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="1"
                    name="trialDays"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.trialDays}
                    placeholder="Enter trial period in days (e.g., 7, 14, 30)"
                  />
                  {formik.touched.trialDays && formik.errors.trialDays ? (
                    <div style={{ color: "red", fontSize: "12px" }}>
                      {formik.errors.trialDays}
                    </div>
                  ) : null}
                </Form.Group>
              </div>

            </div>

            <div className="text-end mt-3">
              <Button
                type="button"
                onClick={handleClose}
                className="btn btn-secondary me-2"
                style={{ marginRight: "10px" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="btn"
                style={{
                  backgroundColor: "#01A54F",
                  color: "#fff",
                  border: "none",
                }}
              >
                {loading
                  ? "Saving..."
                  : isEdit
                  ? "Update"
                  : "Submit"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SubscriptionPackages;

